/**
 * Other printings of this card (sections/printings-ladder.liquid).
 *
 * Finds the same card in the store (One Piece: the card code, Pokemon: card name AND number), keeps only products whose
 * title really contains the same code or the same name + number, leaves out the current product and lists at most 8 of them,
 * most expensive first. The row stays hidden until at least one other printing is found.
 *
 * Nothing is requested when the page loads: the row waits until it is within about 600px of the viewport. It then reads the
 * "card" request of assets/sold-out-alternatives.js (imported by the url in data-helpers, so it is the same, versioned file),
 * which is shared with the sold-out row and Recently viewed, so the whole page needs one request for all of them. A throttled
 * or failed request is retried once there; if it still fails the row stays hidden (no error).
 */

const DEFAULT_MAX = 8;
const CODE_ONLY = /^[A-Za-z]{1,4}\d{0,2}-\d{2,3}$/;
const NUMBER_ONLY = /^\d+(?:\s*\/\s*\d+)?$/;

/**
 * The art / edition words in the title: "Zoro-Juurou (Alternate Art) (OP05-067) - ..." gives "Alternate Art". Card codes
 * and card numbers in brackets are not part of it.
 * @param {string} title
 */
export function descriptorOf(title) {
  /** @type {string[]} */
  const parts = [];
  for (const match of String(title || '').matchAll(/\(([^)]+)\)/g)) {
    const text = (match[1] ?? '').trim();
    if (text && !CODE_ONLY.test(text) && !NUMBER_ONLY.test(text) && !parts.includes(text)) parts.push(text);
  }
  return parts.join(' / ');
}

/** @param {string} word */
function capitalize(word) {
  return word ? word.charAt(0).toUpperCase() + word.slice(1) : '';
}

/**
 * @param {string} tag
 * @param {string} className
 * @param {string} [text]
 */
function make(tag, className, text) {
  const node = document.createElement(tag);
  node.className = className;
  if (text) node.textContent = text;
  return node;
}

/**
 * The nearest ancestor that scrolls vertically inside the page (this theme scrolls .page-wrapper, not the window), or null.
 * An IntersectionObserver only applies its rootMargin to its root, so it has to be told which element scrolls.
 * @param {Element} node
 * @returns {Element | null}
 */
function scrollParent(node) {
  for (let parent = node.parentElement; parent && parent !== document.body && parent !== document.documentElement; parent = parent.parentElement) {
    const overflow = getComputedStyle(parent).overflowY;
    if ((overflow === 'auto' || overflow === 'scroll' || overflow === 'overlay') && parent.scrollHeight > parent.clientHeight + 1) return parent;
  }
  return null;
}

/**
 * Runs `start` once, when the row comes within about 600px of the viewport (an IntersectionObserver with a rootMargin).
 * The row is rendered hidden until it has something to show, and a hidden element is never "visible" to an
 * IntersectionObserver, so a 1px anchor (zero height in the layout) is put just before the hidden wrapper and observed
 * instead. Without IntersectionObserver (or with nothing that can be observed) the row starts when the browser is idle.
 * (Same function as in assets/sold-out-alternatives.js: this file must not load that one before the row is needed.)
 * @param {HTMLElement} element
 * @param {() => void} start
 * @returns {() => void} cancels the wait
 */
function whenNear(element, start) {
  let done = false;
  /** @type {IntersectionObserver | null} */
  let observer = null;
  /** @type {HTMLElement | null} */
  let anchor = null;

  /** @type {HTMLElement | null} */
  let hiddenRoot = null;
  for (let node = /** @type {HTMLElement | null} */ (element); node && node !== document.body; node = node.parentElement) {
    if (node.hidden) hiddenRoot = node;
  }
  let target = element;
  if (hiddenRoot) {
    anchor = document.createElement('div');
    anchor.setAttribute('aria-hidden', 'true');
    anchor.setAttribute('data-lazy-anchor', '');
    anchor.style.cssText = 'display:block;height:1px;margin:0 0 -1px;pointer-events:none';
    hiddenRoot.before(anchor);
    target = anchor;
  }

  const cleanup = () => {
    observer?.disconnect();
    observer = null;
    anchor?.remove();
    anchor = null;
  };
  const go = () => {
    if (done) return;
    done = true;
    cleanup();
    try {
      start();
    } catch (_) {
      // The row is optional: it stays hidden.
    }
  };

  if ('IntersectionObserver' in window && target.getClientRects().length > 0) {
    observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) go();
      },
      { root: scrollParent(target), rootMargin: '600px 0px' }
    );
    observer.observe(target);
  } else if ('requestIdleCallback' in window) {
    window.requestIdleCallback(go, { timeout: 2500 });
  } else {
    window.setTimeout(go, 1500);
  }

  return () => {
    done = true;
    cleanup();
  };
}

class PrintingsLadder extends HTMLElement {
  /** @type {(() => void) | null} */
  #cancel = null;

  connectedCallback() {
    if (this.hasAttribute('data-started')) return;
    this.setAttribute('data-started', '');
    this.#cancel = whenNear(this, () => {
      this.#run().catch(() => {
        this.#finish([]);
      });
    });
  }

  disconnectedCallback() {
    this.#cancel?.();
  }

  async #run() {
    const ds = this.dataset;
    if (!ds.helpers) return;

    const helpers = await import(ds.helpers);
    const me = helpers.identityOf({
      id: ds.id,
      title: ds.title,
      game: ds.game,
      kind: ds.kind,
      lang: ds.lang,
      vendor: ds.vendor,
      number: ds.number,
    });

    const source = ds.source || 'sold-out-alternatives';
    const data = await helpers.cardData(source, me);
    const items = data ? data.items : [];
    const found = items.filter(
      (/** @type {any} */ item) => String(item.id) !== me.id && item.k === 'single' && item.g === me.game && helpers.sameCard(me, item)
    );

    const max = Math.min(DEFAULT_MAX, Math.max(1, parseInt(ds.max || String(DEFAULT_MAX), 10) || DEFAULT_MAX));
    found.sort((/** @type {any} */ a, /** @type {any} */ b) => (b.pm || 0) - (a.pm || 0));
    this.#finish(found.slice(0, max).map((/** @type {any} */ item) => this.#row(item, helpers)));
  }

  /** @param {HTMLLIElement[]} rows */
  #finish(rows) {
    const list = this.querySelector('[data-pl-list]');
    const note = this.querySelector('[data-pl-note]');
    const wrap = this.closest('[data-pl-wrap]');
    if (!list) return;

    if (rows.length) {
      list.replaceChildren(...rows);
      if (note instanceof HTMLElement) note.hidden = true;
      if (wrap instanceof HTMLElement) wrap.hidden = false;
    } else if (this.dataset.design === 'true' && note instanceof HTMLElement) {
      note.hidden = false;
    }
  }

  /**
   * @param {any} item a product record from the data endpoint
   * @param {any} helpers
   */
  #row(item, helpers) {
    const li = document.createElement('li');
    const link = make('a', 'pl__item');
    link.href = helpers.safePath(item.u);

    const thumb = make('span', 'pl__thumb');
    if (item.i) {
      const img = /** @type {HTMLImageElement} */ (make('img', 'pl__img'));
      img.alt = '';
      img.loading = 'lazy';
      img.decoding = 'async';
      img.src = helpers.sizedUrl(item.i, 120);
      img.srcset = [120, 180].map((width) => `${helpers.sizedUrl(item.i, width)} ${width}w`).join(', ');
      img.sizes = '56px';
      thumb.append(img);
    }

    // Art / edition words from the title; the rarity only when the title has none ("Alternate Art" says more than "Rare").
    const descriptor = descriptorOf(item.t);
    const sub = [descriptor || item.r, item.p, item.l && item.l !== 'english' ? capitalize(item.l) : '']
      .filter(Boolean)
      .join(' · ');
    const text = make('span', 'pl__text');
    text.append(make('span', 'pl__label', item.v || item.t));
    if (sub) text.append(make('span', 'pl__sub', sub));

    const stock = helpers.stockLabel(item);
    const cost = helpers.priceText(item);
    const end = make('span', 'pl__end');
    if (cost) end.append(make('span', `pl__price${stock.modifier ? ' pl__price--muted' : ''}`, cost));
    end.append(make('span', `pl__stock${stock.modifier ? ` pl__stock--${stock.modifier}` : ''}`, stock.text));

    link.append(thumb, text, end);
    li.append(link);
    return li;
  }
}

if (!customElements.get('printings-ladder')) customElements.define('printings-ladder', PrintingsLadder);
